import { useQuery } from "@apollo/client";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Warning from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  GridLegacy as Grid,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { FormTextField } from "components/Forms/FormTextField";
import { getWorkOrFirstPhoneNumber } from "encamp-shared/src/utils/phoneNumber";
import {
  Issue,
  ReportDefaultVerifiersQuery,
  TierIiReportDefaultVerifier,
  TierIiReportVerifierInput,
  VerifierFragmentFragment,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import pluralize from "pluralize";
import { REPORT_DEFAULT_VERIFIERS_QUERY } from "queries/tierIIVerification";
import { useMemo, useState, useCallback } from "react";
import { Control } from "react-hook-form";
import { useBulkVerifyInputValidation } from "routes/Staff/Fulfillment/validationHooks/useBulkVerifyInputValidation";

import { IssueListButton } from "./Forms/IssueListButton";
import { VerifierTable } from "./VerifierTable";
import {
  defaultVerifiedAtDate,
  ReportForDefaultDate,
  signatureDateConflict,
} from "./verifyAndSubmitDefaultDate";

type VerifyAndSubmitFormState = {
  personId?: string;
  verifierFullName: string;
  verifierTitle: string;
  verifierPhone: string;
  verifierEmail: string;
  date: string;
};

type BaseProps = {
  onClose: () => void;
  open: boolean;
  loading: boolean;
  defaultVerifier?: {
    person?: VerifierFragmentFragment;
    fullName?: string;
    title?: string;
  };
  /** Reports whose signature-date default is being computed; see verifyAndSubmitDefaultDate. */
  reports: ReportForDefaultDate[];
  reportIds?: string[];
};

type Props = BaseProps &
  (
    | {
        onSubmit: (verifier: TierIiReportVerifierInput) => Promise<void>;
        withConfirmation?: false;
      }
    | {
        onSubmit: (
          verifier: TierIiReportVerifierInput,
          overrideOpenReviews: boolean
        ) => Promise<void>;
        withConfirmation: true;
        skippedDueToOpenReviewsCount: number;
        skippedDueToVerificationCount: number;
        verifiableRowsWithoutOpenReviewsCount: number;
        reportsWithValidationErrorsCount: number;
        /** Reports submitted when the open-review safety check is overridden; defaults to `reports`. */
        overrideReports?: ReportForDefaultDate[];
      }
  );

/** The selected reports' default verifiers, loaded while the dialog is open. */
function useReportDefaultVerifiers(reportIds: string[], open: boolean) {
  const { data, loading, error } = useQuery(REPORT_DEFAULT_VERIFIERS_QUERY, {
    variables: { reportIds },
    skip: !open || reportIds.length === 0,
    fetchPolicy: "network-only",
  });
  const reports = useMemo(() => data?.tierIIReportDefaultVerifiers ?? [], [data]);
  const defaultVerifiers = useMemo(() => {
    const byReportId = new Map<string, TierIiReportDefaultVerifier>();
    for (const { reportId, defaultVerifier } of reports) {
      if (defaultVerifier) byReportId.set(reportId, defaultVerifier);
    }
    return byReportId;
  }, [reports]);
  const reportsWithoutDefault = reports.filter((report) => !report.defaultVerifier);
  const tenantIds = new Set(reports.map((report) => report.tenantId));
  return {
    reports,
    defaultVerifiers,
    reportsWithoutDefault,
    allReportsHaveDefault: reports.length > 0 && reportsWithoutDefault.length === 0,
    tenantId: tenantIds.size === 1 ? [...tenantIds][0] : undefined,
    // Which sections to show depends on the defaults, so the body waits for them.
    defaultsPending: reportIds.length > 0 && (loading || !data),
    defaultsError: error,
  };
}

export const VerifyAndSubmitFormDialog = (props: Props) => {
  const theme = useTheme();
  const validateInput = useBulkVerifyInputValidation();

  const overrideReports =
    (props.withConfirmation ? props.overrideReports : undefined) ?? props.reports;
  // With nothing submittable by default, only the override set can be sent.
  const defaultDate = useMemo(
    () => defaultVerifiedAtDate(props.reports.length > 0 ? props.reports : overrideReports),
    [props.reports, overrideReports]
  );

  const [person, setPerson] = useState<VerifierFragmentFragment | null>(
    props.defaultVerifier?.person ?? null
  );

  const [showConfirmation, setShowConfirmation] = useState(false);

  const reportIds = useMemo(() => props.reportIds ?? [], [props.reportIds]);
  const {
    reports,
    defaultVerifiers,
    reportsWithoutDefault,
    allReportsHaveDefault,
    tenantId,
    defaultsPending,
    defaultsError,
  } = useReportDefaultVerifiers(reportIds, props.open);

  // The server verifies reports that have a default verifier with that default.
  const validator = useCallback(
    async (formData: VerifyAndSubmitFormState): Promise<Issue[]> =>
      reportIds.length > 0 ? validateInput(reportIds, toVerifierInput(formData)) : [],
    [reportIds, validateInput]
  );

  const defaultFormData = useMemo<VerifyAndSubmitFormState>(
    () => ({
      personId: person?.id ?? undefined,
      verifierFullName:
        person?.first && person?.last
          ? `${person?.first} ${person?.last}`
          : (props.defaultVerifier?.fullName ?? ""),
      verifierTitle: person?.title ?? props.defaultVerifier?.title ?? "",
      verifierPhone: getWorkOrFirstPhoneNumber(person?.phones) ?? "",
      verifierEmail: person?.email ?? "",
      date: defaultDate,
    }),
    [person, props.defaultVerifier, defaultDate]
  );

  const form = useValidatingForm(defaultFormData, undefined, validator);
  const date = form.watch("date");
  const dateConflict = useMemo(
    () => signatureDateConflict(props.reports, date),
    [props.reports, date]
  );
  const dateConflictWithOverride = useMemo(
    () => signatureDateConflict(overrideReports, date),
    [overrideReports, date]
  );

  const handleClose = () => {
    if (props.withConfirmation) {
      setShowConfirmation(false);
    }
    props.onClose();
    setPerson(null);
    form.reset();
  };

  const onVerify = async (formState: VerifyAndSubmitFormState) => {
    if (props.withConfirmation) {
      setShowConfirmation(true);
    } else {
      await props.onSubmit(toVerifierInput(formState));
      handleClose();
    }
  };
  const onClearPerson = () => {
    setPerson(null);
    form.reset(defaultFormData);
  };

  const onSelectPerson = (person: VerifierFragmentFragment | null) => {
    setPerson(person);
    form.setValue("personId", person?.id);
    form.setValue(
      "verifierFullName",
      person?.first && person?.last ? `${person?.first} ${person?.last}` : ""
    );
    form.setValue("verifierTitle", person?.title ?? "");
    form.setValue("verifierPhone", getWorkOrFirstPhoneNumber(person?.phones) ?? "");
    form.setValue("verifierEmail", person?.email ?? "");
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth="lg"
      title={
        <Box display="flex">
          Verify & Submit
          <Tooltip title="Use the verifier table to find a verifier, OR enter one manually">
            <IconButton size="small" style={{ marginLeft: theme.spacing(1) }}>
              <InfoOutlined fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      }
      primaryActionButtonProps={{
        onClick: form.handleSubmit(onVerify),
        disabled:
          form.formState.isSubmitting ||
          defaultsPending ||
          !!defaultsError ||
          form.issues.length > 0 ||
          dateConflict !== null,
        loading: props.loading,
        saveText: "Verify & Submit",
      }}
      additionalPrimaryActionElements={[
        <>{form.issues.length > 0 && <IssueListButton issues={form.issues} />}</>,
        !defaultsPending && !allReportsHaveDefault && (
          <Button key="clear-person" onClick={onClearPerson} variant="outlined">
            Clear Person
          </Button>
        ),
      ]}
    >
      <Stack spacing={3}>
        {dateConflict && <SignatureDateAlert message={dateConflict} />}
        {defaultsPending && !defaultsError ? (
          <Stack alignItems="center" justifyContent="center" minHeight={160}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <Box maxWidth={240}>
            <DateField name="date" label="Verified On" control={form.control} />
          </Box>
        )}

        {defaultsError && (
          <Alert severity="error">
            Couldn&apos;t load these reports&apos; default verifiers. Close this dialog and try
            again.
          </Alert>
        )}
        {!defaultsPending && defaultVerifiers.size > 0 && (
          <DefaultVerifiersSection reports={reports} />
        )}

        {!defaultsPending && !allReportsHaveDefault && (
          <ChosenVerifierSection
            control={form.control}
            otherReports={defaultVerifiers.size > 0 ? reportsWithoutDefault : undefined}
            tenantId={tenantId}
            onSelectPerson={onSelectPerson}
          />
        )}
      </Stack>

      {props.withConfirmation && showConfirmation && (
        <ConfirmationDialog
          loading={props.loading}
          onClose={() => setShowConfirmation(false)}
          onSubmit={(overrideOpenReviews) =>
            form.handleSubmit(async (formState) => {
              await props.onSubmit(toVerifierInput(formState), overrideOpenReviews);
              handleClose();
            })()
          }
          skippedDueToOpenReviewsCount={props.skippedDueToOpenReviewsCount}
          skippedDueToVerificationCount={props.skippedDueToVerificationCount}
          verifiableRowsWithoutOpenReviewsCount={props.verifiableRowsWithoutOpenReviewsCount}
          reportsWithValidationErrorsCount={props.reportsWithValidationErrorsCount}
          dateConflict={dateConflict}
          dateConflictWithOverride={dateConflictWithOverride}
        />
      )}
    </Dialog>
  );
};

function toVerifierInput(formData: VerifyAndSubmitFormState): TierIiReportVerifierInput {
  return {
    verifiedAt: formData.date,
    verifierFullName: formData.verifierFullName,
    verifierTitle: formData.verifierTitle,
    verifierEmail: formData.verifierEmail,
    verifierPhone: formData.verifierPhone,
  };
}

type ReportRow = ReportDefaultVerifiersQuery["tierIIReportDefaultVerifiers"][number];

const defaultVerifierColumns: CustomGridColDef<ReportRow>[] = [
  { field: "facilityName", headerName: "Facility", flex: 1.5 },
  { field: "facilityState", headerName: "State", width: 80 },
  {
    field: "verifierFullName",
    headerName: "Default Verifier",
    flex: 1,
    valueGetter: (_value, row) => row.defaultVerifier?.verifierFullName,
  },
  {
    field: "verifierTitle",
    headerName: "Title",
    flex: 1,
    valueGetter: (_value, row) => row.defaultVerifier?.verifierTitle,
  },
  {
    field: "verifierEmail",
    headerName: "Email",
    flex: 1,
    valueGetter: (_value, row) => row.defaultVerifier?.verifierEmail,
  },
  {
    field: "verifierPhone",
    headerName: "Phone",
    width: 130,
    valueGetter: (_value, row) => row.defaultVerifier?.verifierPhone,
  },
];

/** The reports whose verifier is set by a default, which this dialog can't change. */
const DefaultVerifiersSection = ({ reports }: { reports: ReportRow[] }) => {
  const rows = reports.filter((report) => report.defaultVerifier);
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">
        Default verifiers ({rows.length} {pluralize("report", rows.length)})
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {rows.length === 1 ? "This report is" : "These reports are"} verified by the
        organization&apos;s default verifier for {rows.length === 1 ? "its" : "their"} state.
      </Typography>
      <DataGrid
        rows={rows}
        columns={defaultVerifierColumns}
        getRowId={(row) => row.reportId}
        getRowHeight={() => "auto"}
        sx={{ "& .MuiDataGrid-cell": { py: 1, whiteSpace: "normal", wordBreak: "break-word" } }}
        disableRowSelectionOnClick
        pagination
        paginationMode="client"
        pageSizeOptions={[5, 10, 25]}
        hideFooterPagination={false}
        initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
      />
    </Stack>
  );
};

/** The verifier staff choose for the reports without a default. */
const ChosenVerifierSection = (props: {
  control: Control<VerifyAndSubmitFormState>;
  /** Set when some reports use a default, to name the ones this section covers. */
  otherReports?: ReportRow[];
  tenantId?: string;
  onSelectPerson: (person: VerifierFragmentFragment) => void;
}) => (
  <Stack spacing={1}>
    {props.otherReports && (
      <>
        <Typography variant="subtitle1">
          Verifier for the other {props.otherReports.length}{" "}
          {pluralize("report", props.otherReports.length)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {summarizeFacilities(props.otherReports)}
        </Typography>
      </>
    )}
    <Grid container spacing={1}>
      <Grid item xs={12} md={6} lg={4.8}>
        <FormTextField name="verifierFullName" label="Full Name" control={props.control} />
      </Grid>
      <Grid item xs={12} md={6} lg={2.4}>
        <FormTextField name="verifierTitle" label="Title" control={props.control} />
      </Grid>
      <Grid item xs={12} md={6} lg={2.4}>
        <FormTextField name="verifierPhone" label="Phone" control={props.control} />
      </Grid>
      <Grid item xs={12} md={6} lg={2.4}>
        <FormTextField name="verifierEmail" label="Email" control={props.control} />
      </Grid>
    </Grid>
    <VerifierTable onSelectPerson={props.onSelectPerson} tenantId={props.tenantId} />
  </Stack>
);

const LISTED_FACILITY_COUNT = 5;

function summarizeFacilities(reports: ReportRow[]): string {
  const names = reports.slice(0, LISTED_FACILITY_COUNT).map((report) => report.facilityName);
  const moreCount = reports.length - names.length;
  return moreCount > 0 ? `${names.join(", ")}, and ${moreCount} more` : names.join(", ");
}

const SignatureDateAlert = ({ message }: { message: string }) => (
  <Alert severity="error" sx={{ mb: 1 }}>
    {message}
  </Alert>
);

type ConfirmationProps = {
  loading: boolean;
  onClose: () => void;
  onSubmit: (overrideOpenReviews: boolean) => Promise<void>;
  skippedDueToOpenReviewsCount: number;
  skippedDueToVerificationCount: number;
  verifiableRowsWithoutOpenReviewsCount: number;
  reportsWithValidationErrorsCount: number;
  dateConflict: string | null;
  dateConflictWithOverride: string | null;
};

const ConfirmationDialog = (props: ConfirmationProps) => {
  const [overrideOpenReviews, setOverrideOpenReviews] = useState(false);
  const conflict = overrideOpenReviews ? props.dateConflictWithOverride : props.dateConflict;

  const aboutToVerifyCount =
    props.verifiableRowsWithoutOpenReviewsCount +
    (overrideOpenReviews ? props.skippedDueToOpenReviewsCount : 0);

  return (
    <Dialog
      open
      onClose={props.onClose}
      title="Are you sure?"
      primaryActionButtonProps={{
        loading: props.loading,
        disabled: aboutToVerifyCount === 0 || conflict !== null,
        onClick: () => props.onSubmit(overrideOpenReviews),
        saveText: "Yes, I'm sure",
      }}
    >
      {conflict && <SignatureDateAlert message={conflict} />}
      <p>
        You are about to verify <b>{aboutToVerifyCount}</b>{" "}
        {pluralize("report", aboutToVerifyCount)}.
      </p>
      {!!props.skippedDueToVerificationCount && (
        <p>
          <b>{props.skippedDueToVerificationCount}</b>{" "}
          {pluralize("report", props.skippedDueToVerificationCount)}{" "}
          {pluralize("was", props.skippedDueToVerificationCount)} skipped because{" "}
          {props.skippedDueToVerificationCount > 1 ? "they" : "it"}{" "}
          {props.skippedDueToVerificationCount > 1 ? "are" : "is"} not awaiting verification, and{" "}
          {props.skippedDueToVerificationCount > 1 ? "keep their" : "keeps its"} current verifier.
        </p>
      )}
      {!!props.skippedDueToOpenReviewsCount && (
        <>
          {!overrideOpenReviews && (
            <p>
              <b>{props.skippedDueToOpenReviewsCount}</b>{" "}
              {pluralize("report", props.skippedDueToOpenReviewsCount)}{" "}
              {pluralize("was", props.skippedDueToOpenReviewsCount)} skipped because{" "}
              {props.skippedDueToOpenReviewsCount > 1 ? "they" : "it"}{" "}
              {props.skippedDueToOpenReviewsCount > 1 ? "have" : "has"} pending reviews.
            </p>
          )}
          <FormControlLabel
            sx={{ marginLeft: "0px" }}
            control={
              <Checkbox
                sx={{ padding: "9px 4px 9px 0px" }}
                checked={overrideOpenReviews}
                onChange={() => setOverrideOpenReviews(!overrideOpenReviews)}
              />
            }
            label={
              <Typography fontSize={14} fontWeight={500}>
                I want to override this safety check and include{" "}
                {props.skippedDueToOpenReviewsCount}{" "}
                {pluralize("report", props.skippedDueToOpenReviewsCount)} with pending reviews.
              </Typography>
            }
          />
        </>
      )}

      {props.reportsWithValidationErrorsCount > 0 && (
        <Stack direction="row" gap={1}>
          <Warning color="error" />
          <Typography>
            <b>{props.reportsWithValidationErrorsCount}</b>{" "}
            {pluralize("report", props.reportsWithValidationErrorsCount)}{" "}
            {pluralize("has", props.reportsWithValidationErrorsCount)} validation errors.
          </Typography>
        </Stack>
      )}
    </Dialog>
  );
};
